import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { MeetingBriefGetResponse } from '@ai-meeting/shared'
import {
  fetchMeetingBrief,
  setBriefProject,
  toBriefData,
  type BriefData,
  type Meeting,
  type Project,
} from '@/services/meetingBrief'

export type BriefGenerationStatus = 'idle' | 'loading' | 'ready' | 'error' | 'skipped'

export interface UseMeetingBriefResult {
  project: Project | null
  status: BriefGenerationStatus
  data: BriefData | null
  error: string | null
  startedAt: number
  generate: (project: Project, options?: { refresh?: boolean }) => void
  refresh: () => void
}

const POLL_MS = 3_000
/** Auto-brief can occupy the full PENDING lease; the NONE wait uses that window. */
const NONE_POLL_MS = 2_000
const NONE_POLL_MAX_MS = 5 * 60_000 + POLL_MS

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Brief poll aborted', 'AbortError'))
      return
    }
    const timer = window.setTimeout(() => {
      signal.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    const onAbort = () => {
      window.clearTimeout(timer)
      reject(new DOMException('Brief poll aborted', 'AbortError'))
    }
    signal.addEventListener('abort', onAbort, { once: true })
  })
}

function isAbortError(cause: unknown): boolean {
  return cause instanceof DOMException && cause.name === 'AbortError'
}

function isStaleRequest(
  request: number,
  current: number,
  signal: AbortSignal,
  cancelled = false
): boolean {
  return cancelled || request !== current || signal.aborted
}

function projectForKey(projectKey: string, projects: Project[]): Project {
  return (
    projects.find((item) => item.projectKey === projectKey) ?? {
      projectKey,
      name: projectKey,
    }
  )
}

function meetingKey(meeting: Meeting): string {
  return `${meeting.provider}\0${meeting.id}`
}

function applyResponse(
  meeting: Meeting,
  project: Project,
  response: MeetingBriefGetResponse
): { status: BriefGenerationStatus; data: BriefData | null; error: string | null } {
  if (response.status === 'READY' && response.payload) {
    return {
      status: 'ready',
      data: toBriefData(
        meeting,
        project,
        response.payload,
        response.previousMeetingId ?? null,
        response.previousMinutes ?? null
      ),
      error: null,
    }
  }
  if (response.status === 'READY') {
    return { status: 'error', data: null, error: 'generation_failed' }
  }
  if (response.status === 'SKIPPED' || response.errorCode === 'no_briefable_meeting') {
    return { status: 'skipped', data: null, error: null }
  }
  if (response.status === 'FAILED') {
    return { status: 'error', data: null, error: 'generation_failed' }
  }
  return { status: 'loading', data: null, error: null }
}

/**
 * Asks the backend to generate (or reuse) a stored brief, then polls until it
 * is READY. The browser never calls the AI service.
 *
 * @param projects The picker's list, used to name the project of a brief that
 *   was stored by an earlier visit. A brief must render after a reload without
 *   the reader choosing anything again, so the mount read is part of the hook
 *   rather than something the modal remembers.
 */
export function useMeetingBrief(
  meeting: Meeting,
  initialProject: Project | null,
  projects: Project[] = []
): UseMeetingBriefResult {
  const [project, setProject] = useState<Project | null>(initialProject)
  const [status, setStatus] = useState<BriefGenerationStatus>(initialProject ? 'loading' : 'idle')
  const [data, setData] = useState<BriefData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [startedAt, setStartedAt] = useState(() => Date.now())
  const requestNumber = useRef(0)
  const abortRef = useRef<AbortController | null>(null)
  const meetingRef = useRef(meeting)
  meetingRef.current = meeting
  const projectsRef = useRef(projects)
  projectsRef.current = projects
  /**
   * Project remembered when this meeting's modal opened. Parent re-renders sync
   * `initialProject` after `onProjectChange`, which must not re-run the mount
   * read or abort an in-flight generation the reader already started.
   */
  const bootstrapProjectRef = useRef<Project | null>(initialProject)
  const bootstrapMeetingKeyRef = useRef(meetingKey(meeting))
  const currentMeetingKey = meetingKey(meeting)
  if (bootstrapMeetingKeyRef.current !== currentMeetingKey) {
    bootstrapMeetingKeyRef.current = currentMeetingKey
    bootstrapProjectRef.current = initialProject
  }
  /** The picker may load after the brief; the stored key still has to render a name. */
  const selectedProject = useMemo(() => {
    if (!project) return null
    return projects.find((item) => item.projectKey === project.projectKey) ?? project
  }, [project, projects])

  /**
   * Read the stored brief until it stops being PENDING.
   *
   * Shared by the two ways a brief starts: a project the reader just chose
   * (after `PUT`, which kicks generation) and a job the pre-meeting refresh
   * left running. Neither path writes here.
   */
  const poll = useCallback(
    async (nextProject: Project, controller: AbortController, request: number) => {
      let retryFailed = false
      while (!controller.signal.aborted) {
        if (isStaleRequest(request, requestNumber.current, controller.signal)) return
        const response = await fetchMeetingBrief(
          meetingRef.current.provider,
          meetingRef.current.id,
          { signal: controller.signal }
        )
        if (isStaleRequest(request, requestNumber.current, controller.signal)) return
        const next = applyResponse(meetingRef.current, nextProject, response)
        if (next.status === 'error' && response.status === 'FAILED' && !retryFailed) {
          retryFailed = true
          await sleep(POLL_MS, controller.signal)
          continue
        }
        if (next.status !== 'loading') {
          setData(next.data)
          setError(next.error)
          setStatus(next.status)
          return
        }
        await sleep(POLL_MS, controller.signal)
      }
    },
    []
  )

  const run = useCallback((nextProject: Project, options: { refresh?: boolean } = {}) => {
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    const request = ++requestNumber.current

    setProject(nextProject)
    setStatus('loading')
    setError(null)
    setData(null)
    setStartedAt(Date.now())

    void (async () => {
      try {
        await setBriefProject(meetingRef.current.provider, meetingRef.current.id, nextProject.projectKey, {
          refresh: options.refresh === true,
          signal: controller.signal,
        })

        await poll(nextProject, controller, request)
      } catch (cause: unknown) {
        if (isStaleRequest(request, requestNumber.current, controller.signal)) return
        if (isAbortError(cause)) return
        setError(cause instanceof Error ? cause.message : null)
        setStatus('error')
      }
    })()
  }, [poll])

  /**
   * Render the brief the server already stored for this meeting.
   *
   * The modal is opened far more often than a project is chosen: every reload
   * starts from a fresh page, and an already-generated brief has to appear
   * without spending an AI call or asking the reader to pick the same project
   * again. A stored row is authoritative, so this path never writes. A
   * remembered picker choice only generates when nothing is stored yet.
   */
  useEffect(() => {
    const controller = new AbortController()
    abortRef.current?.abort()
    abortRef.current = controller
    const request = ++requestNumber.current
    let cancelled = false

    const bootstrapProject = bootstrapProjectRef.current

    void (async () => {
      let resolved = false
      const applyStored = async (response: MeetingBriefGetResponse) => {
        if (!response.projectKey || response.status === 'NONE') return false
        const nextProject = projectForKey(response.projectKey, projectsRef.current)
        resolved = true
        setProject(nextProject)

        const next = applyResponse(meetingRef.current, nextProject, response)
        if (next.status !== 'loading') {
          setData(next.data)
          setError(next.error)
          setStatus(next.status)
          return true
        }

        setStartedAt(Date.now())
        setStatus('loading')
        await poll(nextProject, controller, request)
        return true
      }

      try {
        const response = await fetchMeetingBrief(meetingRef.current.provider, meetingRef.current.id, {
          signal: controller.signal,
        })
        if (isStaleRequest(request, requestNumber.current, controller.signal, cancelled)) return
        if (await applyStored(response)) return

        if (bootstrapProject) {
          run(bootstrapProject)
          return
        }

        const deadline = Date.now() + NONE_POLL_MAX_MS
        while (Date.now() < deadline) {
          await sleep(NONE_POLL_MS, controller.signal)
          if (isStaleRequest(request, requestNumber.current, controller.signal, cancelled)) return
          const again = await fetchMeetingBrief(
            meetingRef.current.provider,
            meetingRef.current.id,
            { signal: controller.signal }
          )
          if (isStaleRequest(request, requestNumber.current, controller.signal, cancelled)) return
          if (await applyStored(again)) return
        }
        if (isStaleRequest(request, requestNumber.current, controller.signal, cancelled)) return
        setStatus('idle')
      } catch (cause) {
        if (isStaleRequest(request, requestNumber.current, controller.signal, cancelled)) return
        if (isAbortError(cause)) return
        if (!resolved) {
          setStatus('idle')
          return
        }
        setError(cause instanceof Error ? cause.message : null)
        setStatus('error')
      }
    })()

    return () => {
      cancelled = true
      controller.abort()
      abortRef.current?.abort()
    }
  }, [meeting.id, meeting.provider, poll, run])

  const refresh = useCallback(() => {
    if (project) run(project, { refresh: true })
  }, [project, run])

  return {
    project: selectedProject,
    status,
    data,
    error,
    startedAt,
    generate: run,
    refresh,
  }
}
